import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth, getServiceDeskUserId } from "@sd/hooks/useAuth";
import { useServiceDeskUserDisplayMap } from "@sd/hooks/useServiceDeskUserDisplayMap";
import ServiceDeskAPIService, {
  SERVICE_DESK_FORMILY_TICKET_MODEL,
  ptsOpenProjectListQueryKey,
  serviceDeskFormilyTicketGridQueryKey,
  serviceDeskTicketCreateDetailQueryKey,
  serviceDeskTicketCreateListQueryKey,
  serviceDeskTicketCreateListQueryKeyWith,
  serviceDeskTicketNotesQueryKey,
} from "@sd/services/APIs";
import { projectFilterOptionsWithSelection } from "@sd/lib/ptsDropdownOptions";
import {
  ASSIGNED_SELECT_NONE,
  mergeStreamTicketAssignOverrides,
} from "@sd/lib/adminTicketUtils";
import { mergeAgentTicketPatch, overlayLiveTicketFields, pruneAgentTicketOverrides } from "@sd/lib/mergeAgentTicketPatch";
import { putTicketAssignedTo } from "@sd/lib/putTicketAssignedTo";
import { putTicketFormilyStatus } from "@sd/lib/putTicketFormilyStatus";
import { ticketPriorityFieldsFromApi } from "@sd/lib/ticketPriorityFields";
import { extractPayloadArray } from "@sd/lib/userDisplayMap";
import {
  type StreamTicket,
  enrichStreamRowsWithUserMap,
  enrichStreamRowsWithPriorityMap,
  rowFromUnknown,
  streamRowToTicket,
  parseTicketGridTotal,
  extractSlaPolicyGridRows,
} from "@sd/lib/ticketStreamUtils";
import { emptyStatusChange } from "@sd/components/tickets/StatusChangeDialog";
import { DEFAULT_TICKET_STATUS, normalizeStatus, QUEUE_STATUS_FILTER, sanitizeStatusFilterValues } from "@sd/constants/ticketStatuses";
import { DEFAULT_MODULE_FILTER } from "@sd/lib/ticketFilterDefaults";
import { resolvePtsModuleFilterProjectId } from "@sd/hooks/usePtsModuleFilterOptions";
import {
  emptyCreatedDateFilter,
  type CreatedDateFilterState,
} from "@sd/lib/ticketCreatedDateFilter";
import { emptyTicketFilterState } from "@sd/lib/ticketFilterDefaults";
import {
  TICKET_CATEGORY_FILTER_VALUES,
  normalizeCategoryFilterValue,
} from "@sd/lib/ticketCategoryOptions";
import { buildTicketCreateListFilterParams } from "@sd/lib/ticketCreateListFilter";
import {
  buildTrackerReportReturnSearchParams,
  isTrackerReportReturnContext,
  parseTrackerDrillDownSearchParams,
  TRACKER_REPORT_FROM_QUERY,
  TRACKER_REPORT_FROM_VALUE,
} from "@sd/lib/trackerReportDrillDown";
import {
  applyStatusDashboardListContext,
  applyTrackerReportListContext,
  assigneeFiltersFromTrackerAssigneeFilter,
  persistStatusDashboardTicketListContext,
  persistTrackerReportProjectId,
  persistTrackerReportReturnFilters,
  persistTrackerReportTicketListContext,
  readStatusDashboardTicketListContext,
  readTrackerReportProjectId,
  readTrackerReportReturnFilters,
  readTrackerReportTicketListContext,
  trackerReportListContextFromDrillDown,
} from "@sd/lib/ticketListDrillDownStorage";
import {
  emptyStatusDashboardFilters,
  type StatusDashboardFilters,
} from "@sd/lib/statusDashboardApi";
import {
  mergeTrackerReportFilters,
  type TrackerReportFilters,
} from "@sd/lib/trackerReportFilters";
import {
  buildStatusDashboardReturnSearchParams,
  isStatusDashboardReturnContext,
  parseStatusDashboardDrillDownSearchParams,
  parseStatusDashboardReturnContext,
} from "@sd/lib/statusDashboardDrillDown";
import {
  parseWorkbenchSelectedTicketFromSearchParams,
  WORKBENCH_SELECTED_TICKET_QUERY,
} from "@sd/lib/ticketDetailPath";
import {
  agentToolbarScope,
  DEFAULT_CREATE_UPDATE_FILTER,
  ensureDefaultCreatedDateFilter,
  isToolbarFilterQueue,
  readTicketToolbarFilters,
  resolveToolbarRefreshPersistStorageScope,
  stripToolbarFiltersFromSearchParams,
} from "@sd/lib/ticketListFilterStorage";
import { useTicketToolbarFiltersRecoil } from "@sd/hooks/useTicketToolbarFiltersRecoil";
import type { Ticket, TicketStatus } from "@sd/types/ticket";
import { useUser } from "@/context/useUser";
import {
  isDebouncedValuePending,
  useDebouncedValue,
} from "@sd/hooks/useDebouncedValue";

const SEARCH_DEBOUNCE_MS = 400;
const FILTER_DEBOUNCE_MS = 250;

export function useTicketsPage(queue: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { role, isSupportAgent, isLoading: authLoading } = useAuth();
  const isAgentOnly = role === "ServiceDesk_Agent";
  const isSuperAgent = isSupportAgent && !isAgentOnly;
  const { decodedToken } = useUser();
  const queryClient = useQueryClient();

  const { userDisplayMap, agentEmployees, priorityOptions, priorityIdToLabel, requesterFilterOptions } =
    useServiceDeskUserDisplayMap({
      refetchAgentListsOnMount: isSupportAgent,
    });

  const assigneeSelectOptions = agentEmployees;

  const { data: slaPolicyData } = useQuery({
    queryKey: ["sla-policy-map"],
    queryFn: () => ServiceDeskAPIService.formily.getGridData("SLA Policy", 1, 100),
    staleTime: 5 * 60 * 1000,
  });

  const slaPriorityMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const row of extractSlaPolicyGridRows(slaPolicyData)) {
      if (row.id && row.priority) map[String(row.id)] = String(row.priority);
    }
    return map;
  }, [slaPolicyData]);

  const toolbarScope = agentToolbarScope(queue);
  // Agent My Tickets + Super Agent All Tickets: keep toolbar filters after F5 via sessionStorage.
  const refreshPersistStorageScope = resolveToolbarRefreshPersistStorageScope({
    authLoading,
    isAgentOnly,
    isSuperAgent,
    queue,
  });
  const persistFiltersOnRefresh = refreshPersistStorageScope !== null;
  const {
    search,
    setSearch,
    statusFilters,
    setStatusFilters,
    priorityFilters,
    setPriorityFilters,
    categoryFilters,
    setCategoryFilters,
    typeFilter,
    setTypeFilter,
    projectFilters,
    setProjectFilters,
    assigneeFilters,
    setAssigneeFilters,
    requesterFilters,
    setRequesterFilters,
    createdDateFilter,
    setCreatedDateFilter,
    vipFilter,
    setVipFilter,
    slaFilter,
    setSlaFilter,
    moduleFilter,
    setModuleFilter,
    createUpdateFilter,
    setCreateUpdateFilter,
    resetToolbarFilters,
    resetToolbarScope,
    applyDefaultAtScope,
    applyPartial,
  } = useTicketToolbarFiltersRecoil(
    toolbarScope,
    persistFiltersOnRefresh,
    refreshPersistStorageScope ?? undefined
  );

  const toolbarFilterSetters = useMemo(
    () => ({
      setSearch,
      setStatusFilters,
      setPriorityFilters,
      setCategoryFilters,
      setTypeFilter,
      setProjectFilters,
      setAssigneeFilters,
      setRequesterFilters,
      setCreatedDateFilter,
      setVipFilter,
      setSlaFilter,
      setModuleFilter,
      setCreateUpdateFilter,
    }),
    [
      setSearch,
      setStatusFilters,
      setPriorityFilters,
      setCategoryFilters,
      setTypeFilter,
      setProjectFilters,
      setAssigneeFilters,
      setRequesterFilters,
      setCreatedDateFilter,
      setVipFilter,
      setSlaFilter,
      setModuleFilter,
      setCreateUpdateFilter,
    ]
  );

  const [fromTrackerReport, setFromTrackerReport] = useState(() =>
    searchParams.get(TRACKER_REPORT_FROM_QUERY) === TRACKER_REPORT_FROM_VALUE
  );
  const [fromStatusDashboard, setFromStatusDashboard] = useState(() =>
    isStatusDashboardReturnContext(searchParams)
  );
  const [statusDashboardReturnFilters, setStatusDashboardReturnFilters] =
    useState<StatusDashboardFilters>(() =>
      parseStatusDashboardReturnContext(searchParams) ??
        emptyStatusDashboardFilters()
    );
  const [reportReturnProjectId, setReportReturnProjectId] = useState(() =>
    searchParams.get("project")?.trim() || readTrackerReportProjectId() || ""
  );
  const [trackerReportReturnFilters, setTrackerReportReturnFilters] =
    useState<TrackerReportFilters>(() =>
      readTrackerReportReturnFilters() ?? mergeTrackerReportFilters(null)
    );
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  useEffect(() => {
    if (isAgentOnly) return;
    if (!resolvePtsModuleFilterProjectId(projectFilters)) {
      if (moduleFilter !== DEFAULT_MODULE_FILTER) {
        setModuleFilter(DEFAULT_MODULE_FILTER);
      }
    }
  }, [isAgentOnly, projectFilters, moduleFilter, setModuleFilter]);

  const prevQueueRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isSupportAgent) return;

    const returnCtx = isTrackerReportReturnContext(searchParams);
    const statusDashboardCtx = isStatusDashboardReturnContext(searchParams);
    const trackerDrillDown = parseTrackerDrillDownSearchParams(searchParams);
    const statusDrillDown =
      parseStatusDashboardDrillDownSearchParams(searchParams);
    const drillDown = trackerDrillDown ?? statusDrillDown;

    if (!drillDown) {
      if (returnCtx) {
        setFromTrackerReport(true);
        setReportReturnProjectId(returnCtx.projectId);
        const savedReturnFilters = readTrackerReportReturnFilters();
        if (savedReturnFilters) {
          setTrackerReportReturnFilters(savedReturnFilters);
        }
        const savedList = readTrackerReportTicketListContext();
        if (savedList) {
          applyTrackerReportListContext(savedList, {
            setProjectFilters,
            setStatusFilters,
            setTypeFilter,
            setAssigneeFilters,
            setCreatedDateFilter,
          });
        } else if (returnCtx.projectId) {
          setProjectFilters([returnCtx.projectId]);
        }
      } else {
        setFromTrackerReport(false);
      }
      setFromStatusDashboard(statusDashboardCtx);
      if (statusDashboardCtx) {
        const returnFilters =
          parseStatusDashboardReturnContext(searchParams) ??
          emptyStatusDashboardFilters();
        setStatusDashboardReturnFilters(prev => {
          const sameProjects =
            prev.projectFilters.length === returnFilters.projectFilters.length &&
            prev.projectFilters.every(
              (id, i) => id === returnFilters.projectFilters[i]
            );
          const sameDates =
            prev.createdDateFilter.from ===
              returnFilters.createdDateFilter.from &&
            prev.createdDateFilter.to === returnFilters.createdDateFilter.to;
          return sameProjects && sameDates ? prev : returnFilters;
        });
        const savedList = readStatusDashboardTicketListContext();
        if (savedList) {
          applyStatusDashboardListContext(savedList, {
            setProjectFilters,
            setStatusFilters,
            setCreatedDateFilter,
          });
        } else if (returnFilters.projectFilters.length > 0) {
          setProjectFilters(returnFilters.projectFilters);
          setCreatedDateFilter(returnFilters.createdDateFilter);
        }
      } else {
        const clean = stripToolbarFiltersFromSearchParams(searchParams);
        if (clean.toString() !== searchParams.toString()) {
          setSearchParams(clean, { replace: true });
        }
      }
      return;
    }

    const defaults = emptyTicketFilterState();
    setSearch(defaults.search);
    setPriorityFilters(defaults.priorityFilters);
    setCategoryFilters(defaults.categoryFilters);
    setAssigneeFilters(defaults.assigneeFilters);
    setRequesterFilters(defaults.requesterFilters);
    setSlaFilter("all");
    if (isSuperAgent) {
      setCreateUpdateFilter(DEFAULT_CREATE_UPDATE_FILTER);
    }
    setProjectFilters(
      drillDown.projectFilters.length > 0
        ? drillDown.projectFilters
        : defaults.projectFilters
    );
    setStatusFilters(
      sanitizeStatusFilterValues(
        drillDown.statusFilters.length > 0
          ? drillDown.statusFilters
          : defaults.statusFilters
      )
    );
    if (statusDrillDown) {
      setCreatedDateFilter(statusDrillDown.createdDateFilter);
      setTypeFilter(defaults.typeFilter);
      setFromStatusDashboard(true);
      setFromTrackerReport(false);
      setReportReturnProjectId("");
      const returnFilters: StatusDashboardFilters = {
        projectFilters: statusDrillDown.projectFilters,
        createdDateFilter: statusDrillDown.createdDateFilter,
      };
      setStatusDashboardReturnFilters(returnFilters);
      persistStatusDashboardTicketListContext({
        projectFilters: statusDrillDown.projectFilters,
        statusFilters: statusDrillDown.statusFilters,
        createdDateFilter: statusDrillDown.createdDateFilter,
      });
      setSearchParams(buildStatusDashboardReturnSearchParams(returnFilters), {
        replace: true,
      });
    } else {
      const trackerAssignee = trackerDrillDown!.assigneeFilter ?? "all";
      setAssigneeFilters(
        assigneeFiltersFromTrackerAssigneeFilter(trackerAssignee)
      );
      setCreatedDateFilter(
        trackerDrillDown!.createdDateFilter ?? defaults.createdDateFilter
      );
      setTypeFilter(
        trackerDrillDown!.typeFilter !== "all"
          ? trackerDrillDown!.typeFilter
          : defaults.typeFilter
      );

      const fromReport =
        searchParams.get(TRACKER_REPORT_FROM_QUERY) === TRACKER_REPORT_FROM_VALUE;
      const returnProject = drillDown.projectFilters[0] ?? "";
      const returnFilters: TrackerReportFilters = {
        projectFilters: drillDown.projectFilters,
        assigneeFilter: trackerAssignee,
        createdDateFilter: trackerDrillDown!.createdDateFilter ?? emptyCreatedDateFilter(),
      };
      setFromTrackerReport(fromReport);
      setFromStatusDashboard(false);
      setReportReturnProjectId(returnProject);
      setTrackerReportReturnFilters(returnFilters);

      if (fromReport) {
        if (returnProject) persistTrackerReportProjectId(returnProject);
        persistTrackerReportReturnFilters(returnFilters);
        persistTrackerReportTicketListContext(
          trackerReportListContextFromDrillDown({
            projectFilters: drillDown.projectFilters,
            statusFilters: drillDown.statusFilters,
            typeFilter:
              trackerDrillDown!.typeFilter !== "all"
                ? trackerDrillDown!.typeFilter
                : defaults.typeFilter,
            assigneeFilter: trackerAssignee,
            createdDateFilter:
              trackerDrillDown!.createdDateFilter ?? emptyCreatedDateFilter(),
          })
        );
        setSearchParams(buildTrackerReportReturnSearchParams(), {
          replace: true,
        });
      } else {
        setSearchParams({}, { replace: true });
      }
    }
    setPageNum(1);
  }, [
    isSupportAgent,
    isSuperAgent,
    searchParams,
    setSearchParams,
    queue,
    setCreateUpdateFilter,
  ]);

  // Sidebar tab change (Kanban/Report/Dashboard/My Tickets…): reset Recoil filters.
  // Detail page back: same queue — Recoil atom keeps filters (no reset).
  useEffect(() => {
    const drillDown =
      parseTrackerDrillDownSearchParams(searchParams) ??
      parseStatusDashboardDrillDownSearchParams(searchParams);
    const returnCtx =
      isTrackerReportReturnContext(searchParams) ||
      isStatusDashboardReturnContext(searchParams);
    if (drillDown || returnCtx) {
      prevQueueRef.current = queue;
      return;
    }

    const prevQueue = prevQueueRef.current;
    if (prevQueue !== null && prevQueue !== queue) {
      if (isToolbarFilterQueue(prevQueue)) {
        resetToolbarScope(agentToolbarScope(prevQueue));
      }
      if (isToolbarFilterQueue(queue)) {
        if (refreshPersistStorageScope) {
          const saved = readTicketToolbarFilters(refreshPersistStorageScope);
          if (saved) {
            applyPartial(ensureDefaultCreatedDateFilter(saved));
          } else {
            applyDefaultAtScope(agentToolbarScope(queue));
          }
        } else {
          applyDefaultAtScope(agentToolbarScope(queue));
        }
      }
    }

    prevQueueRef.current = queue;
  }, [
    queue,
    refreshPersistStorageScope,
    applyPartial,
    toolbarFilterSetters,
    resetToolbarScope,
    applyDefaultAtScope,
  ]);

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const debouncedStatusFilters = useDebouncedValue(statusFilters, FILTER_DEBOUNCE_MS);
  const debouncedPriorityFilters = useDebouncedValue(priorityFilters, FILTER_DEBOUNCE_MS);
  const debouncedCategoryFilters = useDebouncedValue(categoryFilters, FILTER_DEBOUNCE_MS);
  const debouncedTypeFilter = useDebouncedValue(typeFilter, FILTER_DEBOUNCE_MS);
  const debouncedProjectFilters = useDebouncedValue(projectFilters, FILTER_DEBOUNCE_MS);
  const debouncedAssigneeFilters = useDebouncedValue(assigneeFilters, FILTER_DEBOUNCE_MS);
  const debouncedRequesterFilters = useDebouncedValue(requesterFilters, FILTER_DEBOUNCE_MS);
  const debouncedCreatedDateFilter = useDebouncedValue(
    createdDateFilter,
    FILTER_DEBOUNCE_MS
  );

  const isFilterDebouncing = useMemo(
    () =>
      isDebouncedValuePending(search, debouncedSearch) ||
      isDebouncedValuePending(statusFilters, debouncedStatusFilters) ||
      isDebouncedValuePending(priorityFilters, debouncedPriorityFilters) ||
      isDebouncedValuePending(categoryFilters, debouncedCategoryFilters) ||
      isDebouncedValuePending(typeFilter, debouncedTypeFilter) ||
      isDebouncedValuePending(projectFilters, debouncedProjectFilters) ||
      isDebouncedValuePending(assigneeFilters, debouncedAssigneeFilters) ||
      isDebouncedValuePending(requesterFilters, debouncedRequesterFilters) ||
      isDebouncedValuePending(createdDateFilter, debouncedCreatedDateFilter),
    [
      search,
      debouncedSearch,
      statusFilters,
      debouncedStatusFilters,
      priorityFilters,
      debouncedPriorityFilters,
      categoryFilters,
      debouncedCategoryFilters,
      typeFilter,
      debouncedTypeFilter,
      projectFilters,
      debouncedProjectFilters,
      assigneeFilters,
      debouncedAssigneeFilters,
      requesterFilters,
      debouncedRequesterFilters,
      createdDateFilter,
      debouncedCreatedDateFilter,
    ]
  );

  useEffect(() => {
    setPageNum(1);
  }, [queue, slaFilter, vipFilter, createUpdateFilter]);

  useEffect(() => {
    setPageNum(1);
  }, [
    debouncedSearch,
    debouncedStatusFilters,
    debouncedPriorityFilters,
    debouncedCategoryFilters,
    debouncedTypeFilter,
    debouncedProjectFilters,
    debouncedAssigneeFilters,
    debouncedRequesterFilters,
    debouncedCreatedDateFilter,
  ]);

  const serviceDeskUserId = useMemo(
    () => getServiceDeskUserId(),
    [decodedToken, authLoading, role]
  );

  const agentTicketListExtraParams = useMemo(():
    | Record<string, unknown>
    | undefined => {
    if (!isSupportAgent) return undefined;
    if (!isAgentOnly) {
      const userId = serviceDeskUserId;
      if (queue === "unassigned") return { assigned_to__isnull: true };
      if (queue === "my-tickets" && userId) return { assigned_to: userId };
      if (queue === "closed" && userId) return { assigned_to: userId };
      return undefined;
    }
    // Agent-only tickets are already scoped to the logged-in agent by the
    // backend (via the auth token), so we don't send assigned_to explicitly.
    return undefined;
  }, [isSupportAgent, isAgentOnly, queue, serviceDeskUserId]);

  const ticketListApiParams = useMemo((): Record<string, unknown> => {
    const extra: Record<string, unknown> = {};

    if (!isSupportAgent) {
      if (serviceDeskUserId) extra.requester = serviceDeskUserId;
    } else {
      if (agentTicketListExtraParams) {
        Object.assign(extra, agentTicketListExtraParams);
      }
      if (queue === "vip" || vipFilter === "vip") extra.is_vip = true;
      if (slaFilter === "sla") extra.is_sla = true;
      const queueStatus = QUEUE_STATUS_FILTER[queue];
      if (queueStatus) extra.status = queueStatus;
      if (
        !isAgentOnly &&
        moduleFilter &&
        moduleFilter !== DEFAULT_MODULE_FILTER &&
        resolvePtsModuleFilterProjectId(debouncedProjectFilters)
      ) {
        extra.parent_task = moduleFilter;
      }
    }

    const queueFixesStatus = isSupportAgent && queue === "closed";

    const params = buildTicketCreateListFilterParams({
      search: debouncedSearch,
      page: pageNum,
      pageSize,
      statusFilters: queueFixesStatus ? [] : debouncedStatusFilters,
      priorityFilters: debouncedPriorityFilters,
      categoryFilters: debouncedCategoryFilters,
      typeFilter: debouncedTypeFilter,
      projectFilters: debouncedProjectFilters,
      assigneeFilters: debouncedAssigneeFilters,
      unassignedFilterValue: ASSIGNED_SELECT_NONE,
      requesterFilters: isSupportAgent ? debouncedRequesterFilters : undefined,
      createdDateFilter: debouncedCreatedDateFilter,
      // Super Agent only — same as Admin Ticket Management date-axis filter.
      createUpdateFilter: isSuperAgent ? createUpdateFilter : undefined,
      extra,
    });

    return params;
  }, [
    isSupportAgent,
    isAgentOnly,
    isSuperAgent,
    agentTicketListExtraParams,
    queue,
    slaFilter,
    vipFilter,
    createUpdateFilter,
    moduleFilter,
    serviceDeskUserId,
    debouncedSearch,
    pageNum,
    pageSize,
    debouncedStatusFilters,
    debouncedPriorityFilters,
    debouncedCategoryFilters,
    debouncedTypeFilter,
    debouncedProjectFilters,
    debouncedAssigneeFilters,
    debouncedRequesterFilters,
    debouncedCreatedDateFilter,
  ]);

  const ticketGridQueryKey = useMemo(
    () =>
      serviceDeskTicketCreateListQueryKeyWith(
        ticketListApiParams,
        pageNum,
        pageSize,
        isSupportAgent ? "agent-panel" : "requester-panel",
        queue,
        // Explicit segment so All / On Create / On Update always triggers a new fetch.
        isSuperAgent ? createUpdateFilter : null
      ),
    [
      ticketListApiParams,
      pageNum,
      pageSize,
      isSupportAgent,
      isSuperAgent,
      queue,
      createUpdateFilter,
    ]
  );

  const { data: ptsProjectsData } = useQuery({
    queryKey: ptsOpenProjectListQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.pts.openProjectList();
      return response.data;
    },
    enabled: isSupportAgent,
    staleTime: 5 * 60 * 1000,
  });

  const projectFilterOptions = useMemo(
    () =>
      projectFilterOptionsWithSelection(ptsProjectsData, [
        ...projectFilters,
        ...(reportReturnProjectId ? [reportReturnProjectId] : []),
      ]),
    [ptsProjectsData, projectFilters, reportReturnProjectId]
  );

  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [sortField, setSortField] = useState<keyof StreamTicket>("id");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [localRows, setLocalRows] = useState<StreamTicket[] | null>(null);
  const [detailRow, setDetailRow] = useState<StreamTicket | null>(null);
  const [statusChangeDialog, setStatusChangeDialog] = useState(emptyStatusChange);
  const [agentTicketOverrides, setAgentTicketOverrides] = useState<
    Record<string, Partial<Ticket>>
  >({});
  const [removedTicketIds, setRemovedTicketIds] = useState<Set<string>>(
    () => new Set()
  );
  const removedTicketIdsRef = useRef(removedTicketIds);
  removedTicketIdsRef.current = removedTicketIds;
  const [selectedAgentTicketId, setSelectedAgentTicketId] = useState<string | null>(
    () => parseWorkbenchSelectedTicketFromSearchParams(searchParams)
  );
  const lastSavedAssignedToRef = useRef<Map<string, string | null>>(new Map());
  const [assignMergeVersion, setAssignMergeVersion] = useState(0);

  const ticketsQueryReady =
    !authLoading &&
    Boolean(role) &&
    queue !== "project-status" &&
    queue !== "kanban" &&
    queue !== "report" &&
    (!isAgentOnly || Boolean(serviceDeskUserId) || queue === "unassigned");

  const { data: apiPayload, isPending, isError, isFetching } = useQuery({
    queryKey: ticketGridQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.tickets.listCreatedTickets(
        ticketListApiParams
      );
      if (response?.data === undefined) {
        throw new Error("Failed to load tickets");
      }
      return response.data;
    },
    enabled: ticketsQueryReady,
    staleTime: 30_000,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const baseRows = useMemo(() => {
    if (isError) return [];
    const arr = extractPayloadArray(apiPayload);
    if (arr.length > 0) return arr.map(rowFromUnknown);
    return [];
  }, [apiPayload, isError]);

  const displayRows = useMemo(() => {
    const withUsers = enrichStreamRowsWithUserMap(
      baseRows,
      userDisplayMap,
      assigneeSelectOptions
    );
    const withPriority = enrichStreamRowsWithPriorityMap(
      withUsers,
      priorityIdToLabel
    );
    return mergeStreamTicketAssignOverrides(
      withPriority,
      lastSavedAssignedToRef.current,
      assigneeSelectOptions
    );
  }, [
    baseRows,
    userDisplayMap,
    priorityIdToLabel,
    assigneeSelectOptions,
    assignMergeVersion,
  ]);

  useEffect(() => {
    setLocalRows(null);
  }, [apiPayload, isError, queue]);

  const apiOrderedRows = useMemo(
    () => localRows ?? displayRows,
    [localRows, displayRows]
  );

  const queueFiltered = useMemo(() => {
    if (isSupportAgent) return apiOrderedRows;

    const source = localRows ?? displayRows;
    return source.filter(row => {
      switch (queue) {
        case "all":
          return true;
        case "unassigned":
          return !row.assignedAgent;
        case "breaching":
          return false;
        case "my-tickets":
          if (
            isSupportAgent &&
            agentTicketListExtraParams &&
            typeof agentTicketListExtraParams.assigned_to === "string"
          ) {
            return true;
          }
          return row.assignedAgent === "Current User";
        case "vip":
        case "closed":
          return true;
        case "kanban":
          return false;
        default:
          return true;
      }
    });
  }, [displayRows, localRows, queue, isSupportAgent, agentTicketListExtraParams, apiOrderedRows]);

  useEffect(() => {
    setAgentTicketOverrides(prev =>
      pruneAgentTicketOverrides(prev, displayRows, assigneeSelectOptions)
    );
  }, [apiPayload, displayRows, assigneeSelectOptions]);

  const patchAgentTicket = useCallback((id: string, patch: Partial<Ticket>) => {
    setAgentTicketOverrides(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  const categories = useMemo(() => {
    const fromTickets = queueFiltered
      .map(t => normalizeCategoryFilterValue(t.category))
      .filter(Boolean);
    return [...new Set([...TICKET_CATEGORY_FILTER_VALUES, ...fromTickets])];
  }, [queueFiltered]);

  const filtered = useMemo(() => {
    if (isSupportAgent) return apiOrderedRows;

    const rows = queueFiltered;

    return rows.sort((a, b) => {
      const av = a[sortField] ?? "";
      const bv = b[sortField] ?? "";
      if (sortDirection === "asc") return String(av).localeCompare(String(bv));
      return String(bv).localeCompare(String(av));
    });
  }, [
    apiOrderedRows,
    isSupportAgent,
    queueFiltered,
    sortField,
    sortDirection,
  ]);

  const totalCount = parseTicketGridTotal(apiPayload);
  const totalPages =
    totalCount !== undefined
      ? Math.max(1, Math.ceil(totalCount / pageSize))
      : 1;

  useEffect(() => {
    const ids = new Set(filtered.map(t => t.id));
    setSelectedIds(prev => {
      const next = [...prev].filter(id => ids.has(id));
      if (next.length === prev.size && next.every(id => prev.has(id))) {
        return prev;
      }
      return new Set(next);
    });
  }, [filtered]);

  const agentTickets = useMemo(() => {
    if (!isSupportAgent || queue === "kanban" || queue === "project-status") return [];
    return filtered
      .map(row => {
        const base = streamRowToTicket(row);
        const priorityFields = ticketPriorityFieldsFromApi(
          { priorityId: row.priorityId, priority: row.priority },
          priorityIdToLabel,
          slaPriorityMap
        );
        const enriched: Ticket = {
          ...base,
          ...priorityFields,
        };
        return mergeAgentTicketPatch(enriched, agentTicketOverrides[base.id]);
      })
      .filter(t => !removedTicketIds.has(t.id));
  }, [
    isSupportAgent,
    queue,
    filtered,
    agentTicketOverrides,
    priorityIdToLabel,
    slaPriorityMap,
    removedTicketIds,
  ]);

  const selectAgentTicket = useCallback(
    (ticketId: string) => {
      setSelectedAgentTicketId(ticketId);
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set(WORKBENCH_SELECTED_TICKET_QUERY, ticketId);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /** Drop a ticket from the stream + URL after delete or a 404 detail fetch. */
  const removeAgentTicket = useCallback(
    (ticketId: string) => {
      const id = ticketId.trim();
      if (!id || removedTicketIdsRef.current.has(id)) return;

      setRemovedTicketIds(prev => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setSelectedIds(prev => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setAgentTicketOverrides(prev => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });

      const remaining = agentTickets.filter(t => t.id !== id);
      const idx = agentTickets.findIndex(t => t.id === id);
      const nextId =
        (idx >= 0
          ? agentTickets[idx + 1]?.id ?? agentTickets[idx - 1]?.id
          : remaining[0]?.id) ?? remaining[0]?.id ?? null;

      setSelectedAgentTicketId(prev => (prev === id ? nextId : prev));
      setSearchParams(
        prev => {
          const current = prev.get(WORKBENCH_SELECTED_TICKET_QUERY);
          if (current !== id && nextId && current === nextId) return prev;
          const next = new URLSearchParams(prev);
          if (nextId) next.set(WORKBENCH_SELECTED_TICKET_QUERY, nextId);
          else next.delete(WORKBENCH_SELECTED_TICKET_QUERY);
          return next;
        },
        { replace: true }
      );

      queryClient.removeQueries({
        queryKey: serviceDeskTicketCreateDetailQueryKey(id),
      });
      queryClient.removeQueries({
        queryKey: serviceDeskTicketNotesQueryKey(id),
      });
    },
    [agentTickets, queryClient, setSearchParams]
  );

  useEffect(() => {
    if (!isSupportAgent) return;
    const fromUrl = parseWorkbenchSelectedTicketFromSearchParams(searchParams);
    setSelectedAgentTicketId(prev => {
      // Deleted tickets must not be restored from `?ticket=` (causes 404 + stale detail).
      if (fromUrl && removedTicketIds.has(fromUrl)) {
        if (prev && prev !== fromUrl && agentTickets.some(t => t.id === prev)) {
          return prev;
        }
        return agentTickets[0]?.id ?? null;
      }
      if (prev && agentTickets.some(t => t.id === prev)) return prev;
      if (fromUrl && agentTickets.some(t => t.id === fromUrl)) return fromUrl;
      // Just-created / URL-selected id — keep until it appears in the list.
      if (fromUrl) return fromUrl;
      if (prev) return prev;
      return agentTickets[0]?.id ?? null;
    });
  }, [isSupportAgent, agentTickets, searchParams, removedTicketIds]);

  // Keep `?ticket=` in URL so refresh re-opens the same ticket.
  useEffect(() => {
    if (!isSupportAgent) return;
    const fromUrl = parseWorkbenchSelectedTicketFromSearchParams(searchParams);
    if (!selectedAgentTicketId) {
      if (!fromUrl) return;
      setSearchParams(
        prev => {
          if (!prev.get(WORKBENCH_SELECTED_TICKET_QUERY)) return prev;
          const next = new URLSearchParams(prev);
          next.delete(WORKBENCH_SELECTED_TICKET_QUERY);
          return next;
        },
        { replace: true }
      );
      return;
    }
    if (fromUrl === selectedAgentTicketId) return;
    setSearchParams(
      prev => {
        const current = prev.get(WORKBENCH_SELECTED_TICKET_QUERY);
        if (current === selectedAgentTicketId) return prev;
        const next = new URLSearchParams(prev);
        next.set(WORKBENCH_SELECTED_TICKET_QUERY, selectedAgentTicketId);
        return next;
      },
      { replace: true }
    );
  }, [isSupportAgent, selectedAgentTicketId, searchParams, setSearchParams]);

  const selectedAgentTicket = useMemo(
    () => agentTickets.find(t => t.id === selectedAgentTicketId) ?? null,
    [agentTickets, selectedAgentTicketId]
  );

  const sidebarQueueCounts = useMemo(() => {
    const rows = displayRows;
    return {
      all: rows.length,
      "my-tickets": rows.filter(r => r.assignedAgent && r.assignedAgent !== "—").length,
      vip: rows.filter(r => r.isVip).length,
      unassigned: rows.filter(r => !r.assignedAgent).length,
      open: rows.filter(r => r.status === "new").length,
      closed: rows.filter(r => normalizeStatus(r.status) === "close").length,
    };
  }, [displayRows]);

  const sortSnapshot = useRef({ field: sortField, direction: sortDirection });
  sortSnapshot.current = { field: sortField, direction: sortDirection };

  const handleSort = (field: keyof StreamTicket) => {
    if (isSupportAgent) return;
    const { field: current, direction: dir } = sortSnapshot.current;
    if (current === field) {
      setSortDirection(dir === "desc" ? "asc" : "desc");
      return;
    }
    setSortField(field);
    setSortDirection("desc");
  };

  const syncPatch = useCallback(
    (id: string, patch: Partial<StreamTicket>) => {
      setLocalRows(prev => {
        const base = prev ?? displayRows;
        return base.map(r => (r.id === id ? { ...r, ...patch } : r));
      });
    },
    [displayRows]
  );

  const handleStatusChange = (ticket: StreamTicket, newStatus: TicketStatus) => {
    setStatusChangeDialog({
      open: true,
      ticketId: ticket.id,
      ticketLabel: ticket.uiId ?? ticket.id,
      newStatus,
    });
  };

  const confirmStatusChange = (ticketId: string, newStatus: TicketStatus) => {
    const ticket = (localRows ?? displayRows).find(r => r.id === ticketId);
    const effectiveStatus: TicketStatus =
      !ticket?.assignedAgent && newStatus === "pending_from_client"
        ? "new"
        : newStatus;
    if (ticket) syncPatch(ticketId, { status: effectiveStatus });
    void putTicketFormilyStatus(queryClient, ticketId, effectiveStatus)
      .then(ok => {
        if (!ok) return;
        toast.success(`${ticketId} → ${effectiveStatus}`);
      })
      .catch(() => toast.error("Failed to update status"));
    setStatusChangeDialog(emptyStatusChange());
  };

  const handleAssign = (
    ticketId: string,
    agentId: string,
    agentName: string,
    options?: { updateStatus?: boolean; preservePriorityId?: string }
  ) => {
    const updateStatus = options?.updateStatus ?? true;
    const priorityId = options?.preservePriorityId?.trim();
    const priorityExtra = priorityId ? { priority_id: priorityId } : {};
    if (agentId === "__unassign__") {
      syncPatch(
        ticketId,
        updateStatus
          ? { assignedAgent: "", status: DEFAULT_TICKET_STATUS }
          : { assignedAgent: "" }
      );
      patchAgentTicket(
        ticketId,
        updateStatus
          ? { assignee: undefined, status: DEFAULT_TICKET_STATUS }
          : { assignee: undefined }
      );
      void putTicketAssignedTo(
        queryClient,
        ticketId,
        null,
        updateStatus
          ? { status: DEFAULT_TICKET_STATUS, ...priorityExtra }
          : Object.keys(priorityExtra).length > 0
            ? priorityExtra
            : undefined
      )
        .then(ok => {
          if (!ok) return;
          lastSavedAssignedToRef.current.set(ticketId, null);
          setAssignMergeVersion(v => v + 1);
          toast.success(
            updateStatus
              ? "Unassigned — status set to New"
              : "Ticket unassigned"
          );
        })
        .catch(() => toast.error("Failed to unassign ticket"));
    } else {
      syncPatch(
        ticketId,
        updateStatus
          ? { assignedAgent: agentName, status: "in_progress" }
          : { assignedAgent: agentName }
      );
      patchAgentTicket(
        ticketId,
        updateStatus
          ? { assignee: agentId, status: "in_progress" }
          : { assignee: agentId }
      );
      void putTicketAssignedTo(
        queryClient,
        ticketId,
        agentId,
        {
          ...priorityExtra,
          ...(updateStatus ? { status: "in_progress" } : {}),
        }
      )
        .then(ok => {
          if (!ok) return;
          lastSavedAssignedToRef.current.set(ticketId, agentId);
          setAssignMergeVersion(v => v + 1);
          toast.success(
            updateStatus
              ? `Assigned to ${agentName} — status set to In Progress`
              : `Assigned to ${agentName}`
          );
        })
        .catch(() => toast.error("Failed to assign ticket"));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length && filtered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(t => t.id)));
    }
  };

  const clearAllFilters = useCallback(() => {
    // Full default reset (VIP, SLA, All/On Create/On Update, dates, etc.).
    // Do not patch individual fields after reset — that can re-apply stale state.
    resetToolbarFilters();
    setPageNum(1);
  }, [resetToolbarFilters]);

  const onTicketSchemaSaved = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: serviceDeskTicketCreateListQueryKey,
    });
    queryClient.invalidateQueries({
      queryKey: serviceDeskFormilyTicketGridQueryKey,
    });
    queryClient.invalidateQueries({
      queryKey: ["sd-ticket-created-detail"],
    });
    queryClient.invalidateQueries({
      predicate: query =>
        Array.isArray(query.queryKey) &&
        query.queryKey.includes("agent-panel"),
    });
  }, [queryClient]);

  const mergeAgentDisplayTicket = useCallback(
    (detailTicket: Ticket | null) => {
      if (!selectedAgentTicketId) return null;
      const base = detailTicket ?? selectedAgentTicket;
      if (!base) return null;
      const merged = mergeAgentTicketPatch(
        base,
        agentTicketOverrides[selectedAgentTicketId]
      );
      return overlayLiveTicketFields(merged, selectedAgentTicket);
    },
    [selectedAgentTicket, selectedAgentTicketId, agentTicketOverrides]
  );

  return {
    role,
    isSupportAgent,
    isSuperAgent,
    isAgentOnly,
    search,
    setSearch,
    statusFilters,
    setStatusFilters,
    priorityFilters,
    setPriorityFilters,
    categoryFilters,
    setCategoryFilters,
    typeFilter,
    setTypeFilter,
    projectFilters,
    setProjectFilters,
    projectFilterOptions,
    assigneeFilters,
    setAssigneeFilters,
    requesterFilters,
    setRequesterFilters,
    createdDateFilter,
    setCreatedDateFilter,
    requesterFilterOptions,
    slaFilter,
    setSlaFilter,
    vipFilter,
    setVipFilter,
    createUpdateFilter,
    setCreateUpdateFilter,
    moduleFilter,
    setModuleFilter,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    showQuickAddModal,
    setShowQuickAddModal,
    selectedIds,
    setSelectedIds,
    localRows,
    detailRow,
    setDetailRow,
    statusChangeDialog,
    setStatusChangeDialog,
    selectedAgentTicketId,
    setSelectedAgentTicketId,
    selectAgentTicket,
    removeAgentTicket,
    filtered,
    queueFiltered,
    isPending,
    isFetching,
    isFilterDebouncing,
    categories,
    totalCount,
    totalPages,
    agentTickets,
    selectedAgentTicket,
    assigneeSelectOptions,
    priorityOptions,
    patchAgentTicket,
    sidebarQueueCounts,
    slaPriorityMap,
    ticketGridQueryKey,
    queryClient,
    handleSort,
    handleStatusChange,
    confirmStatusChange,
    handleAssign,
    toggleSelect,
    toggleSelectAll,
    clearAllFilters,
    onTicketSchemaSaved,
    mergeAgentDisplayTicket,
    fromTrackerReport,
    reportReturnProjectId,
    trackerReportReturnFilters,
    fromStatusDashboard,
    statusDashboardReturnFilters,
  };
}
